import Decimal from 'decimal.js';
import { PurchaseOrderLine, TaxInclusiveOption } from './purchase.order.line';
import { TaxRate } from '../taxrate/tax.rate';

/**
 * Named Purchase Order calculator (EF V2 production contract).
 * Shared by Surface recompute and server totals validation.
 *
 * Rules:
 * - Tax modes: Exclusive, Inclusive, No Tax
 * - No Tax: subtotal is total, tax is zero
 * - Rounding: line money to 2dp, document totals to 2dp
 * - Tax rates are percentages (10 = 10%)
 * - Source model is never mutated
 */
export type PurchaseOrderCalculationSource = {
  lines?: PurchaseOrderLine[] | null;
  taxInclusiveOption?: TaxInclusiveOption | null;
  taxRate?: TaxRate | null;
};

export interface PurchaseOrderCalculationResult {
  lines: PurchaseOrderLine[];
  subTotal: number;
  tax: number;
  total: number;
}

export class PurchaseOrderCalculationDomain {

  static calculate(source: PurchaseOrderCalculationSource): PurchaseOrderCalculationResult {

    return calculatePurchaseOrder(source);
  }
}

export function calculatePurchaseOrder(source: PurchaseOrderCalculationSource): PurchaseOrderCalculationResult {

  const taxMode = source?.taxInclusiveOption ?? TaxInclusiveOption.Exclusive;
  const defaultRate = taxRateValue(source?.taxRate);
  const lines = (source?.lines ?? []).map(line => calculateLine(line, taxMode, defaultRate));

  let subTotal = new Decimal(0);
  let tax = new Decimal(0);
  let total = new Decimal(0);

  for (const line of lines) {
    subTotal = subTotal.plus(line.subTotal ?? 0);
    tax = tax.plus(line.tax ?? 0);
    total = total.plus(line.total ?? 0);
  }

  return {
    lines,
    subTotal: round2(subTotal),
    tax: round2(tax),
    total: round2(total),
  };
}

function calculateLine(source: PurchaseOrderLine | undefined, taxMode: TaxInclusiveOption, defaultRate: number): PurchaseOrderLine {

  const quantity = toNumber(source?.quantity, 1);
  const price = toNumber(source?.price, 0);
  const rate = taxMode === TaxInclusiveOption.NoTax ? 0 : taxRateValue(source?.taxRate, defaultRate);
  const lineSubTotal = round2(new Decimal(quantity).times(price));

  let lineTax = 0;
  let lineTotal = lineSubTotal;

  switch (taxMode) {
    case TaxInclusiveOption.Exclusive: {
      lineTax = round2(new Decimal(lineSubTotal).times(rate));
      lineTotal = round2(new Decimal(lineSubTotal).plus(lineTax));
      break;
    }
    case TaxInclusiveOption.Inclusive: {
      const taxPlus = new Decimal(1).plus(rate);
      lineTax = taxPlus.eq(0) ? 0 : round2(new Decimal(lineSubTotal).div(taxPlus).times(rate));
      lineTotal = lineSubTotal;
      break;
    }
    case TaxInclusiveOption.NoTax:
    default: {
      lineTax = 0;
      lineTotal = lineSubTotal;
      break;
    }
  }

  return {
    ...(source ?? {} as PurchaseOrderLine),
    quantity,
    price,
    subTotal: lineSubTotal,
    tax: lineTax,
    total: lineTotal,
  };
}

function taxRateValue(taxRate: TaxRate | null | undefined, fallback = 0): number {

  const rate = taxRate?.rate;
  if (rate == null || !Number.isFinite(rate)) return fallback;
  // TaxRate.rate is always a percentage; guessing fractions turned 1% into 100%.
  return rate / 100;
}

function toNumber(value: unknown, fallback: number): number {

  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function round2(value: Decimal | number): number {

  return new Decimal(value ?? 0).toDP(2).toNumber();
}
